import React, { useState, useEffect, useCallback } from 'react';
import './UserProfile.css';
import { getUsers, saveUsers } from '../utils/storage';
import { getRankMeta } from '../utils/rankConfig';

function UserProfile({ userId, currentUser, onClose, onMessage }) {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    bio: '',
    avatar: '',
    location: '',
    website: '',
    social: {
      discord: '',
      twitter: '',
      youtube: ''
    }
  });

  const loadProfile = useCallback(() => {
    const users = getUsers();
    const user = users.find(u => u.id === userId);
    if (user) {
      setProfile(user);
      setEditData({
        bio: user.bio || '',
        avatar: user.avatar || '',
        location: user.location || '',
        website: user.website || '',
        social: user.social || {
          discord: '',
          twitter: '',
          youtube: ''
        }
      });
    }
  }, [userId]);

  // Fix: react-hooks/exhaustive-deps warning.
  // It is already correct to depend on loadProfile, as loadProfile is stable with useCallback([userId]).
  useEffect(() => {
    loadProfile();
  }, [loadProfile]); // This is correct, as per the recommended fix.

  const handleSave = () => {
    if (!currentUser || currentUser.id !== userId) return;

    const users = getUsers();
    const updatedUsers = users.map(user => {
      if (user.id === userId) {
        return {
          ...user,
          bio: editData.bio,
          avatar: editData.avatar,
          location: editData.location,
          website: editData.website,
          social: editData.social,
          updatedAt: new Date().toISOString()
        };
      }
      return user;
    });

    saveUsers(updatedUsers);
    loadProfile();
    setIsEditing(false);
    alert('Profile updated successfully!');
  };

  const getRoleColor = (role) => getRankMeta(role).color || '#aab1c7';
  const getRoleName = (role) => getRankMeta(role).name || role;

  const getInitials = (username) => {
    if (!username) return '?';
    return username.charAt(0).toUpperCase();
  };

  if (!profile) {
    return (
      <div className="profile-modal-overlay" onClick={onClose}>
        <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
          <div className="profile-loading">
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser && currentUser.id === userId;

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <div className="profile-avatar-section">
            {profile.avatar ? (
              <img src={profile.avatar} alt={profile.discordUsername} className="profile-avatar-img" />
            ) : (
              <div 
                className="profile-avatar" 
                style={{ backgroundColor: getRoleColor(profile.role) }}
              >
                {getInitials(profile.discordUsername)}
              </div>
            )}
            {isOwnProfile && !isEditing && (
              <button className="edit-avatar-btn" onClick={() => setIsEditing(true)}>
                Edit
              </button>
            )}
          </div>
          <div className="profile-info-header">
            <h2>{profile.discordUsername}</h2>
            <span 
              className="profile-role-badge" 
              style={{ backgroundColor: getRoleColor(profile.role) + '20', color: getRoleColor(profile.role) }}
            >
              {getRoleName(profile.role)}
            </span>
            {!isOwnProfile && onMessage && (
              <button className="message-btn" onClick={() => onMessage(profile)}>
                💬 Message
              </button>
            )}
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="profile-content">
          {isEditing && isOwnProfile ? (
            <div className="profile-edit-form">
              <div className="form-group">
                <label>Profile Picture URL</label>
                <input
                  type="url"
                  value={editData.avatar}
                  onChange={(e) => setEditData({ ...editData, avatar: e.target.value })}
                  placeholder="https://example.com/image.png"
                />
                {editData.avatar && (
                  <img src={editData.avatar} alt="Preview" className="avatar-preview" onError={(e) => e.target.style.display = 'none'} />
                )}
              </div>
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={editData.bio}
                  onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                  placeholder="Tell us about yourself..."
                  rows="4"
                  maxLength="500"
                />
                <small>{editData.bio.length}/500 characters</small>
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={editData.location}
                  onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                  placeholder="City, Country"
                  maxLength="50"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={editData.website}
                  onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="form-group">
                <label>Social Links</label>
                <input
                  type="text"
                  value={editData.social.discord}
                  onChange={(e) => setEditData({ ...editData, social: { ...editData.social, discord: e.target.value } })}
                  placeholder="Discord username"
                />
                <input
                  type="text"
                  value={editData.social.twitter}
                  onChange={(e) => setEditData({ ...editData, social: { ...editData.social, twitter: e.target.value } })}
                  placeholder="Twitter @username"
                />
                <input
                  type="text"
                  value={editData.social.youtube}
                  onChange={(e) => setEditData({ ...editData, social: { ...editData.social, youtube: e.target.value } })}
                  placeholder="YouTube channel URL"
                />
              </div>
              <div className="profile-edit-actions">
                <button className="save-btn" onClick={handleSave}>
                  Save Changes
                </button>
                <button className="cancel-btn" onClick={() => { setIsEditing(false); loadProfile(); }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-view">
              {profile.bio && (
                <div className="profile-section">
                  <h3>Bio</h3>
                  <p>{profile.bio}</p>
                </div>
              )}
              <div className="profile-stats">
                <div className="stat-item">
                  <span className="stat-value">{new Date(profile.createdAt).toLocaleDateString()}</span>
                  <span className="stat-label">Joined</span>
                </div>
                {profile.location && (
                  <div className="stat-item">
                    <span className="stat-value">📍 {profile.location}</span>
                    <span className="stat-label">Location</span>
                  </div>
                )}
              </div>
              {(profile.website || profile.social?.discord || profile.social?.twitter || profile.social?.youtube) && (
                <div className="profile-section">
                  <h3>Links</h3>
                  <div className="profile-links">
                    {profile.website && (
                      <a href={profile.website} target="_blank" rel="noopener noreferrer" className="profile-link">
                        🌐 Website
                      </a>
                    )}
                    {profile.social?.discord && (
                      <span className="profile-link">💬 {profile.social.discord}</span>
                    )}
                    {profile.social?.twitter && (
                      <a href={`https://twitter.com/${profile.social.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="profile-link">
                        🐦 {profile.social.twitter}
                      </a>
                    )}
                    {profile.social?.youtube && (
                      <a href={profile.social.youtube} target="_blank" rel="noopener noreferrer" className="profile-link">
                        ▶️ YouTube
                      </a>
                    )}
                  </div>
                </div>
              )}
              {isOwnProfile && (
                <button className="edit-profile-btn" onClick={() => setIsEditing(true)}>
                  ✏️ Edit Profile
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
